import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { AlertController, IonicModule, ModalController, Platform } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { UserGeolocConsentService } from '../../../providers/auth/user-geoloc-consent.service';
import { UserService } from '../../../providers/auth/user.service';
import { Subject, Subscription } from 'rxjs';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { getConfigOrElse } from '../../../providers/config/config.service';

@Component({
  selector: 'cmp',
  templateUrl: './geoloc-consent.modal.html',
  styleUrl: './geoloc-consent.modal.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule, TranslateModule, ReactiveFormsModule],
})
export class GeolocConsentModal implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly platform = inject(Platform);
  private backButtonSubscription: Subscription;

  private readonly userGeolocService = inject(UserGeolocConsentService);
  private readonly userService = inject(UserService);
  private readonly modalController = inject(ModalController);
  private readonly alrtController = inject(AlertController);
  private readonly fb = inject(FormBuilder);

  readonly currentConsentChrono?: boolean;

  consentForm: FormGroup;
  privacyPolicyUrl = signal<string>('');
  geolocationCharterUrl = signal<string>('');

  ngOnInit() {
    this.privacyPolicyUrl.set(getConfigOrElse('PRIVACY_POLICY_URL', ''));
    this.geolocationCharterUrl.set(getConfigOrElse('GEOLOCATION_CHARTER_URL', ''));
    this.consentForm = this.fb.group(
      {
        geolocChrono: [false],
        acknowledged: [false],
      },
      { validators: this.acknowledgedRequiredValidator() }
    );

    if (this.currentConsentChrono !== undefined) {
      this.consentForm.patchValue({
        geolocChrono: this.currentConsentChrono,
      });
    } else {
      this.initHardwareBackButton();
    }
  }

  ngOnDestroy(): void {
    if (this.backButtonSubscription) {
      this.backButtonSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initHardwareBackButton() {
    this.backButtonSubscription = this.platform.backButton.subscribeWithPriority(11000, async () => {});
  }

  private acknowledgedRequiredValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const geolocChrono = group.get('geolocChrono')?.value;
      const acknowledged = group.get('acknowledged')?.value;

      if (geolocChrono && !acknowledged) {
        return { acknowledgedRequired: true };
      }

      return null;
    };
  }

  async saveConsent() {
    try {
      const userId = this.userService.getCurrentUser()!.id;
      const { geolocChrono } = this.consentForm.value;
      await this.userGeolocService.updateGeolocConsents(userId, geolocChrono);
    } catch (e) {
      await this.showAlert();
    } finally {
      await this.closeModal();
    }
  }

  async closeModal() {
    await this.modalController.dismiss();
  }

  private async showAlert() {
    await (
      await this.alrtController.create({
        header: 'Erreur',
        message: 'Une erreur est survenu lors de la sauvegarde du consentement.',
        buttons: ['OK'],
      })
    ).present();
  }
}
